import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { AnalyticsContext, InjectedStyledComponent } from '../../../../../shared/index.js';
import { ITableEvents } from '../../models/index.js';
import { ITableGroup } from '../../models/ITableGroup.js';
import { ITableRow } from '../../models/ITableRow.js';
import Cell from '../Cell/Cell.js';
import ChildRows from '../ChildRows/ChildRows.js';
import RowHeadCell from '../RowHeadCell/RowHeadCell.js';
import { TableComponentBase } from '../TableComponentBase.js';

import { IRowConfiguration, IRowProps, IRowState } from './models/index.js';
import { ROW_CATEGORY_KEY, ROW_COMPONENT_KEY, ROW_PARENT_COMPONENT_KEY } from './Row.meta.js';

type TRowProps = IRowProps & React.TableHTMLAttributes<HTMLTableRowElement>;
export class Row extends TableComponentBase<TRowProps, IRowState, IRowConfiguration> {
	categoryKey = ROW_CATEGORY_KEY;
	componentKey = ROW_COMPONENT_KEY;
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext> | undefined;

	constructor(props: TRowProps) {
		super(props);
		makeObservable(this, {
			childRows: observable.ref,
			expanded: observable.ref,
			fetchingChildren: observable.ref,
			toggleExpanded: action,
			onToggleChange: action,
			fetchChildren: action,
			isLastGrouping: computed,
			row: computed,
			rowIndex: computed,
			grouping: computed,
			columns: computed,
			cellDescribedby: computed,
		});

		this.initialize();
	}

	private initialize = () => {
		if (this.grouping && this.row._expanded === true) {
			this.toggleExpanded(!!this.row._expanded);
		}
	};

	public childRows: ITableRow[] = [];

	public expanded = false;

	public fetchingChildren = false;

	get parentComponentKey(): string | undefined {
		return ROW_PARENT_COMPONENT_KEY;
	}

	get tableEventsKey(): keyof ITableEvents {
		return 'row';
	}

	public toggleExpanded = (explicitValue?: boolean) => {
		if (!this.isLastGrouping) {
			const value = typeof explicitValue === 'boolean' ? explicitValue : !this.expanded;
			const isChange = this.expanded !== value;
			if (isChange) {
				runInAction(() => this.onToggleChange(value, this.row));
			}
			this.expanded = value;
			if (this.expanded) {
				runInAction(() => this.fetchChildren());
			}
		}
	};

	public onToggleChange = (isExpanded: boolean, row: ITableRow): void => {
		const grouping = this.grouping;
		if (grouping && grouping.onToggleChange) {
			grouping.onToggleChange(isExpanded, row);
		}

		const { analyticsContext, suppressAnalytics } = this.props;
		if (this.analyticsHelper && !suppressAnalytics) {
			let context = { isExpanded: isExpanded };
			if (analyticsContext) {
				context = { ...AnalyticsContext, ...context };
			}
			this.analyticsHelper.logEvent({
				event: 'row_toggle',
				eventType: 'click',
				context: context,
			});
		}
	};

	public onExpand = (row: ITableRow): void => {
		if (this.grouping && this.grouping.onExpand) {
			this.grouping.onExpand(row, this.columns);
		}
	};

	public onCollapse = (row: ITableRow): void => {
		if (this.grouping && this.grouping.onCollapse) {
			this.grouping.onCollapse(row, this.columns);
		}
	};

	public fetchChildren = async () => {
		const grouping = this.grouping;
		if (
			grouping &&
			(grouping.getRowsQuery || grouping.fetchChildren) &&
			(!this.childRows || this.childRows.length === 0)
		) {
			this.fetchingChildren = true;
			const { queryStore } = this.table.props;
			if (queryStore) {
				let overridingExecutionOptions = {};
				if (grouping.onFetchChildren) {
					overridingExecutionOptions = await grouping.onFetchChildren(this.row, queryStore);
				}
				if (grouping.fetchChildren) {
					runInAction(async () => {
						if (grouping && grouping.fetchChildren) {
							this.childRows = await grouping.fetchChildren(this.row, this.columns);
						}
					});
				} else {
					let queryResponse = queryStore.query(grouping.getRowsQuery, {
						...overridingExecutionOptions,
						responseContext: { grouping, row: this.row },
					});
					if (grouping.orchestrateChildRows) {
						queryResponse = await grouping.orchestrateChildRows(queryResponse, grouping, this.row);
					}
					runInAction(() => {
						if (queryResponse) {
							this.childRows = queryResponse;
						}
					});
				}
			}
			this.fetchingChildren = false;
		}
	};

	get isLastGrouping(): boolean {
		return (
			!this.grouping || (!this.grouping.getRowsQuery && !this.grouping.fetchChildren) || !this.grouping.childGroup
		);
	}

	get row(): ITableRow {
		return this.props.row;
	}

	get rowIndex(): number {
		return this.props.rowIndex;
	}

	get grouping(): ITableGroup | undefined {
		return this.props.grouping;
	}

	getClassName = (className?: string, rowIndex?: number, grouping?: ITableGroup) => {
		const classNames = [];
		if (className) {
			classNames.push(className);
		}
		const groupClassName =
			grouping && grouping.id && `ksys-table-row-group-${common.commonUtils.paramCase(grouping.id)}`;
		if (groupClassName) {
			classNames.push(groupClassName);
		}
		if (typeof rowIndex === 'number') {
			const isEven = (rowIndex + 1) % 2 === 0;
			const evenClassName = `ksys-table-row-even`;
			const oddClassName = `ksys-table-row-odd`;
			classNames.push(isEven ? evenClassName : oddClassName);
		}
		return classNames.join(' ');
	};

	get columns() {
		return [...((this.grouping && this.grouping.columns) || this.table.columns)];
	}

	get cellDescribedby() {
		const { row } = this.props;
		const rowColumnIdentifier = this.columns && this.columns.find((column) => column.isRowIdentifier);
		if (rowColumnIdentifier) {
			return `${row._rowKey}_${rowColumnIdentifier.id}`;
		}
	}

	render() {
		const {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			children,
			className,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			configuration,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyle,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyleProps,

			table,
			row,
			rowIndex,
			grouping,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			suppressAnalytics,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			analyticsContext,
			...otherProps
		} = this.props;
		const events = this.getEvents(this.eventsForElement, this.row, this.rowIndex);
		const rowClassName = this.getClassName(className, rowIndex, grouping);
		const ariaRowIndex =
			table.ariaStartingRowIndex !== undefined ? table.ariaStartingRowIndex + (rowIndex + 1) : undefined;
		if (ariaRowIndex !== undefined) {
			otherProps['aria-rowindex'] = ariaRowIndex;
		}
		const { childRows, table: _table, grouping: _grouping, expanded, isLastGrouping, fetchingChildren } = this;
		return (
			<AnalyticsContext.Consumer>
				{(analyticsHelper) => {
					this.analyticsHelper = analyticsHelper;
					return (
						<Fragment>
							<tr className={rowClassName} {...events} {...otherProps}>
								{this.columns.map((column, columnIndex) => {
									const cellKey = this.getCellKey(row, rowIndex, column);
									// only set the aria-describedby if the column is not the identifier
									const cellValue = this.table.logic.getValue(
										row,
										column,
										this.table.translationContext,
										this,
										this.table,
										this.cellDescribedby,
										rowIndex,
										grouping,
									);
									const cellStyleProps = this.getCellStyleProps(
										row,
										rowIndex,
										column,
										columnIndex,
										cellValue,
									);
									const cellProps = {
										...cellStyleProps,
										table,
										column,
										columnIndex,
										row,
										rowIndex,
										cellValue,
										key: cellKey,
									};
									if (column.isRowHeader) {
										return <RowHeadCell {...cellProps} />;
									}
									return <Cell {...cellProps} />;
								})}
							</tr>
							{!isLastGrouping && !fetchingChildren && expanded && childRows && childRows.length > 0 && (
								<ChildRows table={_table} rows={childRows} grouping={_grouping} />
							)}
						</Fragment>
					);
				}}
			</AnalyticsContext.Consumer>
		);
	}
}

const StyledRow = InjectedStyledComponent(observer(Row), ROW_CATEGORY_KEY, ROW_PARENT_COMPONENT_KEY, [
	ROW_COMPONENT_KEY,
]);
export default StyledRow;
